import type { DirectoryEntry, ShareRecipient } from "./resource-sharing"
import { entryKey } from "./share-entry-key"

export type NameMatch =
  | { kind: "one", entry: DirectoryEntry }
  | { kind: "many", candidates: DirectoryEntry[] }
  | { kind: "none" }
  | { kind: "hidden" }

export type RowDecision = { kind: "undecided" } | { kind: "skip" } | { kind: "pick", entry: DirectoryEntry }

export type ChooseReason = "ambiguous" | "unknown" | "hidden"

interface ChooseStatus {
  kind: "choose"
  reason: ChooseReason
  options: DirectoryEntry[]
  decision: RowDecision
}

export type AddRowStatus =
  | { kind: "grant", entry: DirectoryEntry }
  | { kind: "hasAccess", entry: DirectoryEntry }
  | { kind: "conflict", entry: DirectoryEntry }
  | { kind: "skipped" }
  | ChooseStatus

export type RemoveRowStatus =
  | { kind: "revoke", entry: DirectoryEntry }
  | { kind: "conflict", entry: DirectoryEntry }
  | { kind: "owner" }
  | { kind: "notShared" }
  | { kind: "skipped" }
  | ChooseStatus

export interface ShareDraftRow<S> {
  key: string
  said: string
  status: S
  isPicked: boolean
}

export interface ShareDraftPlan {
  adds: ShareDraftRow<AddRowStatus>[]
  removes: ShareDraftRow<RemoveRowStatus>[]
  grants: DirectoryEntry[]
  revokes: DirectoryEntry[]
  undecided: number
  conflicts: number
  skipped: number
}

export interface ShareDraftPlanInput {
  add: string[]
  remove: string[]
  directory: DirectoryEntry[]
  recipients: ShareRecipient[]
  userId: string | null
  decisions: Partial<Record<string, RowDecision>>
}

const UNDECIDED: RowDecision = { kind: "undecided" }
const INVISIBLE_CHARACTERS = /[\p{Cc}\p{Cf}]/gu
const HAS_INVISIBLE_CHARACTER = /[\p{Cc}\p{Cf}]/u

export function visibleText(value: string): string {
  return value.replace(INVISIBLE_CHARACTERS, "")
}

export function rowKey(side: "add" | "remove", index: number): string {
  return `${side}:${index}`
}

function folded(value: string): string {
  return value.normalize("NFKC").toLocaleLowerCase("en-US").normalize("NFKC").trim().replace(/\s+/g, " ")
}

function matchOf(matches: DirectoryEntry[]): NameMatch {
  if (matches.length === 1) return { kind: "one", entry: matches[0] }
  if (matches.length > 1) return { kind: "many", candidates: matches }
  return { kind: "none" }
}

export function matchName(said: string, pool: DirectoryEntry[]): NameMatch {
  if (HAS_INVISIBLE_CHARACTER.test(said)) return { kind: "hidden" }
  const wanted = folded(said)
  if (wanted === "") return { kind: "none" }
  const byEmail = pool.filter(entry => entry.email != null && folded(entry.email) === wanted)
  if (byEmail.length > 0) return matchOf(byEmail)
  return matchOf(pool.filter(entry => folded(entry.name) === wanted))
}

function uniqueByKey(entries: DirectoryEntry[]): DirectoryEntry[] {
  const seen = new Set<string>()
  return entries.filter((entry) => {
    const key = entryKey(entry)
    if (seen.has(key)) return false
    seen.add(key)
    return true
  })
}

function validPick(decision: RowDecision, options: DirectoryEntry[]): DirectoryEntry | null {
  if (decision.kind !== "pick") return null
  const wanted = entryKey(decision.entry)
  return options.find(option => entryKey(option) === wanted) ?? null
}

function chooseStatus(match: Exclude<NameMatch, { kind: "one" }>, pool: DirectoryEntry[], decision: RowDecision): ChooseStatus {
  if (match.kind === "many") return { kind: "choose", reason: "ambiguous", options: match.candidates, decision }
  return { kind: "choose", reason: match.kind === "hidden" ? "hidden" : "unknown", options: pool, decision }
}

interface Resolved<S> {
  status: S
  isPicked: boolean
}

function resolveAdd(said: string, decision: RowDecision, pool: DirectoryEntry[], directory: DirectoryEntry[], isAlreadyIn: (entry: DirectoryEntry) => boolean): Resolved<AddRowStatus> {
  if (decision.kind === "skip") return { status: { kind: "skipped" }, isPicked: false }
  const settled = (entry: DirectoryEntry): AddRowStatus => (isAlreadyIn(entry) ? { kind: "hasAccess", entry } : { kind: "grant", entry })
  const match = matchName(said, directory)
  if (match.kind === "one") return { status: settled(match.entry), isPicked: false }
  const choose = chooseStatus(match, pool, decision)
  const picked = validPick(decision, choose.options)
  if (picked !== null) return { status: settled(picked), isPicked: true }
  return { status: { ...choose, decision: UNDECIDED }, isPicked: false }
}

function resolveRemove(said: string, decision: RowDecision, removable: DirectoryEntry[], owners: DirectoryEntry[], directory: DirectoryEntry[]): Resolved<RemoveRowStatus> {
  if (decision.kind === "skip") return { status: { kind: "skipped" }, isPicked: false }
  const match = matchName(said, removable)
  if (match.kind === "one") return { status: { kind: "revoke", entry: match.entry }, isPicked: false }
  if (match.kind === "none" && matchName(said, owners).kind === "one") return { status: { kind: "owner" }, isPicked: false }
  if (removable.length === 0) return { status: { kind: "notShared" }, isPicked: false }
  if (match.kind === "none" && matchName(said, directory).kind === "one") return { status: { kind: "notShared" }, isPicked: false }
  const choose = chooseStatus(match, removable, decision)
  const picked = validPick(decision, choose.options)
  if (picked !== null) return { status: { kind: "revoke", entry: picked }, isPicked: true }
  return { status: { ...choose, decision: UNDECIDED }, isPicked: false }
}

function targetOf(status: AddRowStatus | RemoveRowStatus): DirectoryEntry | null {
  if (status.kind === "grant" || status.kind === "hasAccess" || status.kind === "revoke") return status.entry
  return null
}

export function planShareDraft({ add, remove, directory, recipients, userId, decisions }: ShareDraftPlanInput): ShareDraftPlan {
  const withAccess = new Set(recipients.map(entryKey))
  const isSelf = (entry: DirectoryEntry) => entry.kind === "member" && entry.id === userId
  const isAlreadyIn = (entry: DirectoryEntry) => withAccess.has(entryKey(entry)) || isSelf(entry)
  const removable = recipients.filter(recipient => !recipient.isOwner && !isSelf(recipient))
  const owners = recipients.filter(recipient => recipient.isOwner || isSelf(recipient))
  const addPool = directory.filter(entry => !isSelf(entry))

  const resolvedAdds = add.map((said, index) => {
    const key = rowKey("add", index)
    return { key, said, ...resolveAdd(said, decisions[key] ?? UNDECIDED, addPool, directory, isAlreadyIn) }
  })
  const resolvedRemoves = remove.map((said, index) => {
    const key = rowKey("remove", index)
    return { key, said, ...resolveRemove(said, decisions[key] ?? UNDECIDED, removable, owners, directory) }
  })

  const addTargets = new Set(resolvedAdds.flatMap(row => [targetOf(row.status)].filter(entry => entry !== null)).map(entryKey))
  const removeTargets = new Set(resolvedRemoves.flatMap(row => [targetOf(row.status)].filter(entry => entry !== null)).map(entryKey))
  const isConflicted = (entry: DirectoryEntry | null) => entry !== null && addTargets.has(entryKey(entry)) && removeTargets.has(entryKey(entry))

  const adds = resolvedAdds.map((row): ShareDraftRow<AddRowStatus> => {
    const target = targetOf(row.status)
    return target !== null && isConflicted(target) ? { ...row, status: { kind: "conflict", entry: target } } : row
  })
  const removes = resolvedRemoves.map((row): ShareDraftRow<RemoveRowStatus> => {
    const target = targetOf(row.status)
    return target !== null && isConflicted(target) ? { ...row, status: { kind: "conflict", entry: target } } : row
  })

  const statuses = [...adds.map(row => row.status), ...removes.map(row => row.status)]
  return {
    adds,
    removes,
    grants: uniqueByKey(adds.flatMap(row => (row.status.kind === "grant" ? [row.status.entry] : []))),
    revokes: uniqueByKey(removes.flatMap(row => (row.status.kind === "revoke" ? [row.status.entry] : []))),
    undecided: statuses.filter(status => status.kind === "choose").length,
    conflicts: statuses.filter(status => status.kind === "conflict").length,
    skipped: statuses.filter(status => status.kind === "skipped").length,
  }
}

function keysOf(entries: DirectoryEntry[]): string {
  return entries.map(entryKey).sort().join(",")
}

export function isSameChange(first: ShareDraftPlan, second: ShareDraftPlan): boolean {
  return keysOf(first.grants) === keysOf(second.grants)
    && keysOf(first.revokes) === keysOf(second.revokes)
    && first.undecided === second.undecided
    && first.conflicts === second.conflicts
}

export function isAlreadyApplied(plan: ShareDraftPlan): boolean {
  return plan.undecided === 0 && plan.conflicts === 0 && plan.skipped === 0 && plan.grants.length === 0 && plan.revokes.length === 0
    && plan.removes.every(row => row.status.kind !== "owner")
}

export function canApply(plan: ShareDraftPlan): boolean {
  return plan.undecided === 0 && plan.conflicts === 0 && plan.grants.length + plan.revokes.length > 0
}
